import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
    Mail, Search, RefreshCw, AlertTriangle, CheckCircle, Zap, XCircle, Filter,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';

const STATUS_LABELS = {
    success: { label: 'Envoyé', cls: 'bg-green-500/20 text-green-400', icon: CheckCircle },
    failed: { label: 'Échec', cls: 'bg-red-500/20 text-red-400', icon: XCircle },
    mocked: { label: 'Mock', cls: 'bg-amber-500/20 text-amber-400', icon: Zap },
};

const fmtDate = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
};

export default function EmailLogsPage() {
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [filterDays, setFilterDays] = useState(30);
    const [query, setQuery] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { days: filterDays, limit: 200 };
            if (filterStatus !== 'all') params.status = filterStatus;
            if (filterType !== 'all') params.email_type = filterType;
            if (query.trim()) params.q = query.trim();
            const [logsRes, statsRes] = await Promise.all([
                api.get('/super-admin/email-logs', { params }),
                api.get('/super-admin/email-logs/stats', { params: { days: filterDays } }),
            ]);
            setLogs(logsRes.data.logs || []);
            setStats(statsRes.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur de chargement');
        } finally {
            setLoading(false);
        }
    }, [filterStatus, filterType, filterDays, query]);

    useEffect(() => { load(); }, [load]);

    const emailTypes = useMemo(() => {
        const set = new Set(stats?.by_type?.map(t => t.type) || []);
        return Array.from(set);
    }, [stats]);

    return (
        <motion.div
            className="space-y-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            data-testid="email-logs-page"
        >
            {/* Header */}
            <div>
                <div className="flex items-center gap-3 mb-2">
                    <Mail className="w-7 h-7 text-[#681793]" />
                    <h1 className="text-3xl font-black text-white" data-testid="email-logs-title">
                        Email Logs
                    </h1>
                </div>
                <p className="text-muted-foreground">
                    Monitoring temps réel des envois Resend (success / failed / mocked).
                </p>
            </div>

            {/* Stats cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard
                        label="Total"
                        value={stats.total}
                        sub={`${filterDays}j`}
                        color="text-white"
                        testid="stat-total"
                    />
                    <StatCard
                        label="Envoyés"
                        value={stats.by_status.success}
                        sub="✓"
                        color="text-green-400"
                        testid="stat-success"
                    />
                    <StatCard
                        label="Échecs"
                        value={stats.by_status.failed}
                        sub={`${stats.failure_rate}%`}
                        color={stats.by_status.failed > 0 ? 'text-red-400' : 'text-muted-foreground'}
                        testid="stat-failed"
                    />
                    <StatCard
                        label="Mocked"
                        value={stats.by_status.mocked}
                        sub="No API key"
                        color="text-amber-400"
                        testid="stat-mocked"
                    />
                </div>
            )}

            {/* Filters */}
            <div className="bg-card border border-border rounded-2xl p-4 flex flex-wrap gap-3 items-center">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[140px] bg-background border-border" data-testid="filter-status">
                        <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                        <SelectItem value="all">Tous statuts</SelectItem>
                        <SelectItem value="success">Envoyés</SelectItem>
                        <SelectItem value="failed">Échecs</SelectItem>
                        <SelectItem value="mocked">Mocked</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-[180px] bg-background border-border" data-testid="filter-type">
                        <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-72">
                        <SelectItem value="all">Tous types</SelectItem>
                        {emailTypes.map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={String(filterDays)} onValueChange={(v) => setFilterDays(parseInt(v))}>
                    <SelectTrigger className="w-[120px] bg-background border-border" data-testid="filter-days">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                        <SelectItem value="7">7 jours</SelectItem>
                        <SelectItem value="30">30 jours</SelectItem>
                        <SelectItem value="90">90 jours</SelectItem>
                        <SelectItem value="365">1 an</SelectItem>
                    </SelectContent>
                </Select>
                <div className="flex-1 min-w-[200px] relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Rechercher email ou objet…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm text-white focus:border-[#681793] focus:outline-none"
                        data-testid="filter-search"
                    />
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-button">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
            </div>

            {/* Logs table */}
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-muted-foreground">Chargement…</div>
                ) : logs.length === 0 ? (
                    <div className="p-12 text-center text-muted-foreground" data-testid="logs-empty">
                        <Mail className="w-10 h-10 mx-auto mb-3 opacity-40" />
                        <p>Aucun log d'envoi sur la période.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="logs-table">
                            <thead className="bg-background border-b border-border">
                                <tr className="text-left text-muted-foreground">
                                    <th className="px-4 py-3 font-medium">Date</th>
                                    <th className="px-4 py-3 font-medium">Statut</th>
                                    <th className="px-4 py-3 font-medium">Type</th>
                                    <th className="px-4 py-3 font-medium">Destinataire</th>
                                    <th className="px-4 py-3 font-medium">Objet</th>
                                    <th className="px-4 py-3 font-medium">Détail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log, idx) => {
                                    const meta = STATUS_LABELS[log.status] || { label: log.status, cls: 'bg-slate-500/20 text-slate-300', icon: AlertTriangle };
                                    const Icon = meta.icon;
                                    return (
                                        <tr
                                            key={log.email_id || `${log.created_at}-${idx}`}
                                            className="border-b border-border/40 last:border-0 hover:bg-background/40"
                                            data-testid={`log-row-${idx}`}
                                        >
                                            <td className="px-4 py-3 text-foreground whitespace-nowrap text-xs">
                                                {fmtDate(log.created_at)}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge className={`${meta.cls} border-0 gap-1`}>
                                                    <Icon className="w-3 h-3" />
                                                    {meta.label}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground text-xs">
                                                {log.email_type}
                                            </td>
                                            <td className="px-4 py-3 text-foreground truncate max-w-[200px]" title={log.to_email}>
                                                {log.to_email}
                                            </td>
                                            <td className="px-4 py-3 text-foreground/80 truncate max-w-[260px]" title={log.subject}>
                                                {log.subject}
                                            </td>
                                            <td className="px-4 py-3 text-xs">
                                                {log.error ? (
                                                    <span className="text-red-400" title={log.error}>
                                                        {log.error.slice(0, 50)}…
                                                    </span>
                                                ) : log.email_id ? (
                                                    <span className="text-muted-foreground font-mono">
                                                        {log.email_id.slice(0, 12)}…
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </motion.div>
    );
}

function StatCard({ label, value, sub, color, testid }) {
    return (
        <div className="bg-card border border-border rounded-xl p-4" data-testid={testid}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className={`text-3xl font-black ${color} mt-1`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
    );
}
